import { useEffect } from 'react';
import { reaction } from 'mobx';
import { useStores } from '@/contexts/StoreContext';
import {
  saveCurrentProject,
  getCurrentProjectId,
} from '@/utils/projectManager';
import { isLocalStorageAvailable } from '@/utils/persistence';

export const usePersistence = () => {
  const { projectStore, uiStore } = useStores();

  // Set up auto-saving to the current project
  useEffect(() => {
    if (!isLocalStorageAvailable()) {
      console.warn(
        'localStorage is not available. Persistence features disabled.'
      );
      return;
    }

    // Auto-save elements to the current project when they change
    const elementsReactionDisposer = reaction(
      () => projectStore.elements.map(el => ({ ...el })), // Deep-enough copy to track changes
      elements => {
        const currentProjectId = getCurrentProjectId();
        if (currentProjectId) {
          saveCurrentProject(elements, {
            zoom: uiStore.zoom,
            offsetX: uiStore.canvasOffset.x,
            offsetY: uiStore.canvasOffset.y,
          });
        }
      },
      { delay: 1000 } // Debounce saving
    );

    // Auto-save viewport to the current project when it changes
    const viewportReactionDisposer = reaction(
      () => ({
        zoom: uiStore.zoom,
        offsetX: uiStore.canvasOffset.x,
        offsetY: uiStore.canvasOffset.y,
      }),
      viewportState => {
        const currentProjectId = getCurrentProjectId();
        if (currentProjectId) {
          saveCurrentProject(projectStore.elements, viewportState);
        }
      },
      { delay: 1000 } // Debounce saving
    );

    // Cleanup reactions on unmount
    return () => {
      elementsReactionDisposer();
      viewportReactionDisposer();
    };
  }, [projectStore, uiStore]);

  // Manual save function for export
  const saveNow = () => {
    if (isLocalStorageAvailable()) {
      const currentProjectId = getCurrentProjectId();
      if (currentProjectId) {
        saveCurrentProject(projectStore.elements, {
          zoom: uiStore.zoom,
          offsetX: uiStore.canvasOffset.x,
          offsetY: uiStore.canvasOffset.y,
        });
      }
    }
  };

  return {
    saveNow,
    isStorageAvailable: isLocalStorageAvailable(),
  };
};
